import { BubbleMenu, Editor } from '@tiptap/react';
import {
  BoldIcon,
  CodeIcon,
  ItalicIcon,
  ListIcon,
  ListOrderedIcon,
  StrikethroughIcon,
  SubscriptIcon,
  SuperscriptIcon,
  TableIcon,
  MinusIcon,
  ChevronDownIcon,
  ImageIcon,
  VideoIcon,
} from 'lucide-react';

import { Toggle } from '@/components/ui/toggle';
import { cn } from '@/lib/utils';

interface BubbleMenuBarProps {
  editor: Editor;
  className?: string;
}

export function BubbleMenuBar({ editor, className }: BubbleMenuBarProps) {
  if (!editor) {
    return null;
  }

  return (
    <BubbleMenu
      className={cn(
        'flex w-fit divide-x divide-border rounded-md border border-border bg-background shadow-md text-xs size-full p-0 overflow-hidden',
        className
      )}
      editor={editor}
      tippyOptions={{ duration: 100, trigger: 'focus' }}
    >
      {/* Basic formatting */}
      <div className="flex items-center">
        <Toggle
          size="sm"
          pressed={editor.isActive('bold')}
          onPressedChange={() => editor.chain().focus().toggleBold().run()}
          className="rounded-none m-0 p-2 pl-2.5 h-full"
        >
          <BoldIcon className="h-3.5 w-3.5" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive('italic')}
          onPressedChange={() => editor.chain().focus().toggleItalic().run()}
          className="rounded-none m-0 p-2 h-full"
        >
          <ItalicIcon className="h-3.5 w-3.5" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive('strike')}
          onPressedChange={() => editor.chain().focus().toggleStrike().run()}
          className="rounded-none m-0 p-2 h-full"
        >
          <StrikethroughIcon className="h-3.5 w-3.5" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive('code')}
          onPressedChange={() => editor.chain().focus().toggleCode().run()}
          className="rounded-none m-0 p-2 h-full"
        >
          <CodeIcon className="h-3.5 w-3.5" />
        </Toggle>
      </div>

      {/* Script formatting */}
      <div className="flex items-center">
        <Toggle
          size="sm"
          pressed={editor.isActive('subscript')}
          onPressedChange={() => editor.chain().focus().toggleSubscript().run()}
          className="rounded-none m-0 p-2 h-full"
        >
          <SubscriptIcon className="h-3.5 w-3.5" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive('superscript')}
          onPressedChange={() => editor.chain().focus().toggleSuperscript().run()}
          className="rounded-none m-0 p-2 h-full"
        >
          <SuperscriptIcon className="h-3.5 w-3.5" />
        </Toggle>
      </div>

      {/* Lists */}
      <div className="flex items-center">
        <Toggle
          size="sm"
          pressed={editor.isActive('bulletList')}
          onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
          className="rounded-none m-0 p-2 h-full"
        >
          <ListIcon className="h-3.5 w-3.5" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive('orderedList')}
          onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
          className="rounded-none m-0 p-2 h-full"
        >
          <ListOrderedIcon className="h-3.5 w-3.5" />
        </Toggle>
      </div>

      {/* Special elements */}
      <div className="flex items-center">
        <Toggle
          size="sm"
          pressed={false}
          onPressedChange={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          className="rounded-none m-0 p-2 h-full"
        >
          <TableIcon className="h-3.5 w-3.5" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={false}
          onPressedChange={() => editor.chain().focus().setHorizontalRule().run()}
          className="rounded-none m-0 p-2 h-full"
        >
          <MinusIcon className="h-3.5 w-3.5" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={false}
          onPressedChange={() => {
            editor
              .chain()
              .focus()
              .insertContent(
                `<details>
<summary>Click to expand</summary>

Your content here...

</details>`
              )
              .run();
          }}
          className="rounded-none m-0 p-2 pr-2.5 h-full"
          title="Insert collapsible section"
        >
          <ChevronDownIcon className="h-3.5 w-3.5" />
        </Toggle>
      </div>

      {/* Media upload buttons */}
      <div className="flex items-center">
        <Toggle
          size="sm"
          pressed={false}
          onPressedChange={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.multiple = true;
            input.onchange = (e) => {
              const files = Array.from((e.target as HTMLInputElement).files || []);
              if (files.length > 0) {
                // Additional validation since accept attribute is just a UI hint
                const validImageFiles = files.filter(file => file.type.startsWith('image/'));
                if (validImageFiles.length > 0) {
                  editor.commands.uploadImages(validImageFiles);
                }
              }
            };
            input.click();
          }}
          className="rounded-none m-0 p-2 h-full"
          title="Upload image"
        >
          <ImageIcon className="h-3.5 w-3.5" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={false}
          onPressedChange={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'video/*';
            input.multiple = true;
            input.onchange = (e) => {
              const files = Array.from((e.target as HTMLInputElement).files || []);
              if (files.length > 0) {
                // Additional validation since accept attribute is just a UI hint
                const validVideoFiles = files.filter(file => file.type.startsWith('video/'));
                if (validVideoFiles.length > 0) {
                  editor.commands.uploadVideos(validVideoFiles);
                }
              }
            };
            input.click();
          }}
          className="rounded-none m-0 p-2 pr-2.5 h-full"
          title="Upload video"
        >
          <VideoIcon className="h-3.5 w-3.5" />
        </Toggle>
      </div>
    </BubbleMenu>
  );
}
